import React, { useState } from 'react'
import { Button, Card, CardContent, Divider, Grid, TextField } from '@mui/material'
import { SearchOutlined } from '@mui/icons-material'
import { Formik } from 'formik'
import {
  VehicleDto,
  VehicleNeedsInHouseAdditionalProgrammingDetailsDto,
  VehicleNeedsInHouseAdditionalProgrammingDetailsValidationSchema
} from 'dtos'
import { LabelField, GreenText } from 'components'
import * as controllers from 'controllers'
import { useLayout } from 'context'

interface Step1StartAdditionalProgrammingProps {
  needsInHouseAdditionalProgrammingDetails: VehicleNeedsInHouseAdditionalProgrammingDetailsDto
  onNext: () => void
  vehicle: VehicleDto
  vehicleGuid?: string
}

export default function Step1AdditionalProgrammingCompleted({
  needsInHouseAdditionalProgrammingDetails,
  onNext,
  vehicle,
  vehicleGuid
}: Step1StartAdditionalProgrammingProps) {
  const layout = useLayout()
  const [search, setSearch] = useState<string>('')
  const [searchError, setSearchError] = useState<string>('')
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const searchVehicle = (setValues: any) => {
    layout.setIsLayoutLoading(true)
    controllers
      .getNeedsInHouseAdditionalProgrammingSearch(search)
      .then(response => {
        if (response.value) {
          const vehicleLast6OfVin = vehicle.vin?.slice(-6)
          if (search === vehicleLast6OfVin || search === vehicle.importerStockNumber) {
            setValues(response.value)
            setHasSearched(true)
            setSearch('')
            setSearchError('')
          } else {
            setValues(new VehicleDto())
            setHasSearched(false)
            setSearchError('Entered VIN does not match the last 6 of the VIN or Importer Stock #')
          }
        } else {
          setValues(new VehicleDto())
          setHasSearched(false)
          setSearchError('No results found')
        }
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }

  const isAdditionalProgrammingStarted = Boolean(needsInHouseAdditionalProgrammingDetails?.additionalProgrammingInHouseStarted)
  const buttonLabel = isAdditionalProgrammingStarted
    ? loading
      ? 'Completing Additional Programming...'
      : 'Complete Additional Programming'
    : loading
    ? 'Starting Additional Programming...'
    : 'Start Additional Programming'

  return (
    <Formik
      enableReinitialize={true}
      initialValues={needsInHouseAdditionalProgrammingDetails}
      validate={values => {}}
      onSubmit={(values, formikHelpers) => {
        onNext()
        formikHelpers.setSubmitting(false)
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={VehicleNeedsInHouseAdditionalProgrammingDetailsValidationSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        setTouched,
        values,
        validateForm
      }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    error={Boolean(searchError)}
                    helperText={searchError}
                    fullWidth
                    label='Last 6 of VIN or Importer Stock #'
                    onChange={handleSearchChange}
                    value={search}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        searchVehicle(setValues)
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    endIcon={<SearchOutlined />}
                    color='primary'
                    fullWidth
                    onClick={() => searchVehicle(setValues)}
                    variant='contained'
                  >
                    SEARCH
                  </Button>
                </Grid>

                {hasSearched && vehicle && (
                  <>
                    <Grid item xs={12}>
                      <LabelField label='VIN' value={vehicle?.vin} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Year' value={vehicle?.year} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Make' value={vehicle?.make} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Model' value={vehicle?.model} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField
                        label='Additional Programming Started'
                        value={needsInHouseAdditionalProgrammingDetails?.additionalProgrammingInHouseStarted ? 'Yes' : 'No'}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} marginTop={1}>
                  <Button
                    color='primary'
                    fullWidth
                    onClick={onNext}
                    variant='contained'
                    disabled={!hasSearched || !values.vin || isSubmitting}
                  >
                    {buttonLabel}
                  </Button>
                </Grid>

                <Grid item xs={12} marginTop={1}>
                  <GreenText variant='outlined'>
                    {`**Last 6 of VIN/Importer Stock Number Search:**

A free-text input field for entering either the last six 
characters of the VIN or the Importer Stock Number of a 
vehicle.

**SEARCH Button:**

Clicking the Search button performs a check to match the 
entered Last 6 of VIN or Importer Stock Number with the 
currently selected vehicle.

If a match is found, the VIN, Year, Make, and Model fields 
will auto-populate with the vehicle’s details.

If no match is found, red text will appear, displaying: “No 
Match Found.”

**VIN:**

A read-only label that displays the VIN of the searched 
vehicle.

**Year:**

A read-only label that displays the Year of the searched 
vehicle.

**Make:**

A read-only label that displays the Make of the searched 
vehicle.

**Model:**

A read-only label that displays the Model of the searched 
vehicle.

**Additional Programming Started:**

A dynamic status label that updates based on the progress 
of additional programming:

If Additional Programming Started = No, the button will 
display “Start Additional Programming.”

If Additional Programming Started = Yes, the button will 
display “Complete Additional Programming.”

**${isAdditionalProgrammingStarted ? 'COMPLETE ADDITIONAL PROGRAMMING' : 'START ADDITIONAL PROGRAMMING'} Button:**

Clicking this button will:

${
  isAdditionalProgrammingStarted
    ? `Update the “Additional Programming Complete” step on the Cluster Timeline.

Save the name of the user who pressed this button along with the current date and time into the database.

Return the user to the “Needs In-House Additional Programming” To-Do List screen.`
    : `Set the "Additional Programming Started" field to "Yes."

Update the “Additional Programming In-House Started” step on the Cluster Timeline.

Save the name of the user who pressed this button along with the current date and time into the database.

Return the user to the “Needs In-House Additional Programming” To-Do List screen.`
}
`}
                  </GreenText>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  )
}
